import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { DownArrow } from "../../components/svgs/common";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import ProcessingMessageBox from "../../components/chat/LoadingMessage/ProcessingMessageBox";
import { useFocusEffect } from "@react-navigation/native";
import { MessageBubble } from "../../components/chatNewInterface/interfaceComponents/messageBubble/MessageBubble";

interface Message {
  id: string;
  sender?: string;
  content?: string;
  created?: string;
}

interface MessagesContainerProps {
  messages: Message[];
  isStreaming?: boolean;
  currentStreamingMessageId?: string | null;
  resetAutoScroll?: number; // When this changes, reset user interaction and resume auto-scroll
}

const CONTAINER_PADDING_BOTTOM = 16;

const mapMessageToBubble = (message: Message) => ({
  role: (message.sender === "human" ? "user" : "assistant") as "user" | "assistant",
  content: message.content ?? "",
  timestamp: message.created ? new Date(message.created) : new Date(),
  isArchived: false,
});

const MessagesContainer = ({
  messages,
  isStreaming = false,
  currentStreamingMessageId,
  resetAutoScroll,
}: MessagesContainerProps) => {
  const scrollViewRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [downButton, setDownButton] = useState(false);
  const [humanMessageOffset, setHumanMessageOffset] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(undefined);
  const [parentHeight, setParentHeight] = useState(0);

  const [isUserInteracted, setIsUserInteracted] = useState(false);

  const isAtBottomRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      return () => {
        setIsUserInteracted(false);
      };
    }, [])
  );

  const handleScroll = useCallback((event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;

    if (!isAtBottom) {
      setIsUserScrolling(true);
      setDownButton(true);
      setIsUserInteracted(true); // User scrolled up, stop auto-scrolling
    } else {
      setIsUserScrolling(false);
      setDownButton(false);
    }

    isAtBottomRef.current = isAtBottom;
  }, []);

  const scrollToBottom = (animated = false) => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollToEnd({ animated });
    }
  };

  const scrollToBottomButton = () => {
    setDownButton(false);
    scrollToBottom(true);
  };

  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.setAttribute("data-sensitive", "true");
    }
  }, []);

  // Reset user interaction when resetAutoScroll changes (e.g., when new message is submitted)
  useEffect(() => {
    if (resetAutoScroll !== undefined) {
      setIsUserInteracted(false);
    }
  }, [resetAutoScroll]);

  useEffect(() => {
    if (isUserInteracted && humanMessageOffset > 0 && !isUserScrolling) {
      scrollViewRef.current.scrollTo({ y: humanMessageOffset });
    } else {
      scrollToBottom();
    }
  }, [humanMessageOffset]);

  useEffect(() => {
    if (!messages) return;
    if (messages.length === 0) return;

    if (!isUserInteracted) {
      scrollToBottom();
    }
  }, [messages]);

  const handleOnLayout = (event, item, array) => {
    const isLastHumanMessage = array.findLast((item) => item.sender === "human")?.id === item.id;
    if (isLastHumanMessage) {
      const humanMessageOffsetFromTop = event.nativeEvent.layout.y;
      setHumanMessageOffset(humanMessageOffsetFromTop);
      setScrollViewHeight(humanMessageOffsetFromTop + parentHeight - CONTAINER_PADDING_BOTTOM);
    }
  };

  const streamingMessage = currentStreamingMessageId
    ? messages.find((message) => message.id === currentStreamingMessageId)
    : undefined;
  const shouldShowProcessingFallback = isStreaming && (!streamingMessage || !streamingMessage.content);

  return (
    <View
      style={[styles.container, { paddingBottom: CONTAINER_PADDING_BOTTOM }]}
      onLayout={(event) => setParentHeight(event.nativeEvent.layout.height)}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        maintainVisibleContentPosition={{
          minIndexForVisible: 0,
          autoscrollToTopThreshold: 10,
        }}
        showsVerticalScrollIndicator={true}
        contentContainerStyle={{ height: scrollViewHeight }}
        onContentSizeChange={() => {
          if (!isUserInteracted) {
            scrollToBottom();
          }
        }}>
        <View style={styles.view}>
          {messages?.map((item, index, array) => (
            <View
              onLayout={(event) => {
                handleOnLayout(event, item, array);
              }}
              key={item.id || index}>
              <MessageBubble {...mapMessageToBubble(item)} />
            </View>
          ))}
          {shouldShowProcessingFallback && (
            <View>
              <CustomSpacing type="vertical" size="s" />
              <ProcessingMessageBox />
            </View>
          )}
        </View>
      </ScrollView>
      {downButton && (
        <View style={styles.buttonContainer}>
          <CustomButton
            styleType="primary"
            style={styles.button}
            leftIcon={<DownArrow />}
            onPress={scrollToBottomButton}
            rightIcon={undefined}
            text={undefined}
            textProps={undefined}
            textStyle={undefined}
            disabled={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    height: 20,
    paddingHorizontal: 10,
    zIndex: 0,
  },
  view: {
    width: "100%",
    alignSelf: "center",
    maxWidth: 768,
    paddingHorizontal: 10,
  },
  button: {
    justifyContent: "center",
    position: "absolute",
    alignItems: "center",
    bottom: 10,
    width: 30,
    height: 30,
    borderRadius: 25,
    zIndex: 5,
  },
  buttonContainer: {
    alignItems: "center",
  },
});

export default MessagesContainer;
